<template>
  <div id="login">

    <div class="row h-100 justify-content-center align-items-center">
      <div class="login-form forgot-password-form form">
        <h3 class="text-center brand">
          <img src="/frontend/img/logo-icon.svg" />
        </h3>

        <!-- A link that arrived without its token cannot be completed, and the form
             below would only ever answer 422. Say so, and point at the one thing that
             does help. -->
        <template v-if="!hasLink">
          <div class="error text-center">
            <div>{{ brokenLinkMessage }}</div>
          </div>

          <div class="form-group text-center">
            <router-link class="btn btn-default" :to="{ name: 'forgot-password' }">
              <span>{{ i18n.app.components.login.forgot_password }}</span>
            </router-link>
          </div>
        </template>

        <template v-else>
          <div class="form-group">
            <input type="text" readonly v-model="login" name="login" id="login" class="form-control" />
          </div>

          <!-- Not tied to a field: an expired or tampered reset link comes back under
               `login`, which is readonly here, so it would land on a box the user cannot
               act on. -->
          <div class="error" v-if="generalErrors.length">
            <div v-for="(message, index) in generalErrors" :key="index">{{ message }}</div>
          </div>

          <div class="form-group">
            <input type="password" name="password" id="password" class="form-control rounded-0"
              :class="{ 'has-error': errors.password }"
              :placeholder="i18n.app.common.password" v-model="password" />
            <div class="error" v-if="errors.password">
              <div v-for="(message, index) in errors.password" :key="index">{{ message }}</div>
            </div>
          </div>

          <div class="form-group">
            <input type="password" name="password_confirmation" id="password_confirmation" class="form-control"
              :class="{ 'has-error': errors.password_confirmation }"
              :placeholder="i18n.app.common.confirm_password" v-model="passwordConfirm" />
            <div class="error" v-if="errors.password_confirmation">
              <div v-for="(message, index) in errors.password_confirmation" :key="index">{{ message }}</div>
            </div>
          </div>

          <div class="form-group text-center">
            <button :disabled="state.loading" @click="onResetPasswordPress" class="btn btn-default"
              :class="state.loading && 'load-spinner'" type="button">
              <span>{{ i18n.app.components.login.reset_password }}</span>
            </button>
          </div>
        </template>

        <div class="text-center footer-brand">
          <img src="/frontend/img/logo-grey.svg" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import {
    mapActions
  } from "vuex";
  import dialogMixin from "@/mixins/dialog.mixin";
  import { LoginReason, loginLocation } from "@/boot/session";

  export default {
    data() {
      return {
        // loading declared up front: Vue 2 does not track keys added to an object
        // afterwards, so `state.loading = true` never reached the button.
        state: { loading: false },
        token: this.$route.query.token ?? "",
        // `email` is what links issued before RR-349 carry.
        login: this.$route.query.login ?? this.$route.query.email ?? "",
        password: "",
        passwordConfirm: "",
        errors: {}
      };
    },
    mixins: [dialogMixin],
    computed: {
      /**
       * Whether this page was opened through a reset link that still has both halves
       * of what the broker needs.
       *
       * The token is single-use and lives only in the query string, so anything that
       * navigates away takes it with it. The session bootstrap used to do exactly
       * that; now that it leaves credential pages alone, this covers what is left -
       * a truncated link, or one a mail client rewrote.
       */
      hasLink() {
        return Boolean(this.token && this.login);
      },
      brokenLinkMessage() {
        return this.i18n?.passwords?.token ?? this.i18n?.messages?.something_went_wrong ?? "";
      },
      /** Everything the server rejected that does not belong to a field on this form. */
      generalErrors() {
        return Object.keys(this.errors)
          .filter(field => field !== "password" && field !== "password_confirmation")
          .flatMap(field => this.errors[field]);
      }
    },
    methods: {
      onResetPasswordPress() {
        this.state.loading = true;
        this.errors = {};

        this._resetPassword({
          token: this.token,
          login: this.login,
          password: this.password,
          password_confirmation: this.passwordConfirm
        })
        .then(data => {
          // The login form is the only place left to say the reset worked
          // (RR-330); it renders notices.login_reasons.password_changed.
          this.$router.push(loginLocation(LoginReason.PASSWORD_CHANGED))
        })
        .catch(response => {
          // 422 carries why, per field, already in the user's language. This used to
          // be logged to the console and nowhere else, so a password below the
          // minimum looked like a button that did nothing.
          this.errors = response?.data?.errors ?? {};

          if (!Object.keys(this.errors).length) {
            this.errors = { form: [this.i18n.messages.something_went_wrong] };
          }
        })
        .finally(() => (this.state.loading = false));
      },
      ...mapActions({
        _resetPassword: "resetPassword",
      })
    }
  };

</script>
